import map from 'lodash/map'
import * as React from 'react'
import {mmap} from '@freckle/maybe'
import {H1} from '@frontrow/console/src/components/typography'
import {Button} from '@frontrow/console/src/components/button'
import Restricted from '@frontrow/console/src/components/common/restricted'
import {Languages} from '@frontrow/console/src/entities/helpers/languages'
import {type ColumnDef} from '@tanstack/react-table'

import {NumberFilter} from '@frontrow/console/src/components/common/data-table/filters'
import DataTable, {
  type FilterT,
  type FetchDataParamsT,
  type DataTableColumnMeta
} from '@frontrow/console/src/components/common/data-table'
import ErrorAlert from '@frontrow/console/src/components/common/error-alert'
import SchoolAdminCreateButtonModal from '@frontrow/console/src/components/pages/school-admins-page/school-admin-create-button-modal'
import SchoolAdminsFormButtonModal from '@frontrow/console/src/components/pages/school-admins-page/school-admins-form-button-modal'
import SchoolAdminsDistrictButtonModal from '@frontrow/console/src/components/pages/school-admins-page/school-admins-district-button-modal'
import SchoolAdminsDeleteButtonModal from '@frontrow/console/src/components/pages/school-admins-page/school-admins-delete-button-modal'
import SchoolAdminsSchoolsButtonModal from '@frontrow/console/src/components/pages/school-admins-page/school-admins-schools-button-modal'
import SchoolAdminsVerificationButtonModal from '@frontrow/console/src/components/pages/school-admins-page/school-admins-verification-button-modal'
import SchoolAdminProfile from '@frontrow/console/src/components/pages/school-admins-page/school-admin-profile'
import {
  type DetailedSchoolAdminT,
  type SchoolAdminUpdateT
} from '@frontrow/console/src/models/school-admin'
import {
  actionBarStyle,
  newSchoolAdminContainer,
  schoolAdminsDataTableContainer
} from './school-admins-data-table.module.scss'

type Props = {
  schoolAdminsData: Array<DetailedSchoolAdminT>
  defaultFiltered: Array<FilterT>
  newSchoolAdmin: DetailedSchoolAdminT | undefined | null
  fetchData: (fetchDataParams: FetchDataParamsT) => void
  updateSchoolAdmin: (
    schoolAdminId: number,
    updatedSchoolAdmin: SchoolAdminUpdateT
  ) => Promise<void>
  updateSchoolAdminDistricts: (
    schoolAdminId: number,
    addDistrictId: number,
    deleteDistrictId: number
  ) => Promise<void>
  updateSchoolAdminSchools: (
    schoolAdminId: number,
    addSchoolIds: Array<number>,
    deleteSchoolIds: Array<number>
  ) => Promise<void>
  impersonateSchoolAdmin: (schoolAdminEmail: string) => void
  isLoading: boolean
  errors: Array<string>
  removeSchoolAdmin: (schoolAdminId: number) => Promise<void>
  schoolAdminNewAdmin: (newSchoolAdmin: DetailedSchoolAdminT) => void
}

export default function SchoolAdminsDataTable(props: Props): React.ReactElement {
  const {
    schoolAdminsData,
    defaultFiltered,
    newSchoolAdmin,
    fetchData,
    updateSchoolAdmin,
    updateSchoolAdminDistricts,
    updateSchoolAdminSchools,
    impersonateSchoolAdmin,
    isLoading,
    errors,
    removeSchoolAdmin,
    schoolAdminNewAdmin
  } = props

  const columns: ColumnDef<DetailedSchoolAdminT, unknown>[] = [
    {
      id: 'schoolAdmin.id',
      header: 'Admin Id',
      accessorFn: sa => sa.id,
      meta: {Filter: NumberFilter} satisfies DataTableColumnMeta
    },
    {
      id: 'schoolAdmin.firstName',
      header: 'First Name',
      accessorFn: sa => sa.firstName
    },
    {
      id: 'schoolAdmin.lastName',
      header: 'Last Name',
      accessorFn: sa => sa.lastName
    },
    {
      id: 'schoolAdmin.email',
      header: 'Email',
      accessorFn: sa => sa.email
    },
    {
      id: 'schoolAdmin.confirmed',
      header: 'Confirmed',
      accessorFn: sa => sa.confirmed.toString(),
      enableColumnFilter: false
    },
    {
      id: 'schoolAdmin.verified',
      header: 'Verified',
      accessorFn: () => null,
      cell: ctx => {
        const schoolAdmin = ctx.row.original
        const verificationLink =
          schoolAdmin.verificationUrl !== null && schoolAdmin.verificationUrl !== undefined ? (
            <SchoolAdminsVerificationButtonModal schoolAdmin={schoolAdmin} />
          ) : null
        return (
          <Restricted to={['admin', 'support']}>
            <span>
              {schoolAdmin.verified ? 'true' : 'false'} {verificationLink}
            </span>
          </Restricted>
        )
      },
      enableColumnFilter: false,
      enableSorting: false
    },
    {
      id: 'schoolAdmin.schoolId',
      header: 'School Id',
      accessorFn: sa => mmap(s => s.schoolId, sa),
      meta: {Filter: NumberFilter} satisfies DataTableColumnMeta
    },
    {
      id: 'schoolAdmin.schoolName',
      header: 'School Name',
      accessorFn: sa => mmap(s => s.schoolName, sa)
    },
    {
      id: 'schoolAdmin.districtId',
      header: 'District Id',
      accessorFn: sa => mmap(s => s.districtId, sa),
      meta: {Filter: NumberFilter} satisfies DataTableColumnMeta
    },
    {
      id: 'schoolAdmin.districtName',
      header: 'District Name',
      accessorFn: sa => mmap(s => s.districtName, sa),
      enableColumnFilter: false
    },
    {
      id: 'schoolAdmin.postalCode',
      header: 'Postal Code',
      accessorFn: sa => mmap(s => s.postalCode, sa),
      enableColumnFilter: false,
      maxSize: 90
    },
    {
      id: 'schoolAdmin.administrativeArea',
      header: 'Administrative Area',
      accessorFn: sa => mmap(s => s.administrativeArea, sa),
      enableColumnFilter: false,
      maxSize: 90
    },
    {
      id: 'schoolAdmin.language',
      header: 'Language',
      accessorFn: sa => Languages.toString(sa.language),
      enableColumnFilter: false
    },
    {
      id: 'action',
      header: 'Action',
      accessorFn: () => null,
      cell: ctx => {
        const schoolAdminToUpdate = ctx.row.original
        return (
          <React.Fragment>
            <Restricted to={['admin', 'support']}>
              <Button
                title="Impersonate School Admin"
                style="link"
                onClick={() => {
                  impersonateSchoolAdmin(schoolAdminToUpdate.email)
                }}
              >
                <i className="fa fa-sign-in fa-lg" />
              </Button>
            </Restricted>
            <SchoolAdminsFormButtonModal
              schoolAdminToUpdate={schoolAdminToUpdate}
              updateSchoolAdmin={updateSchoolAdmin}
            />
            <SchoolAdminsSchoolsButtonModal
              schoolAdminToUpdate={schoolAdminToUpdate}
              updateSchoolAdminSchools={updateSchoolAdminSchools}
            />
            <SchoolAdminsDistrictButtonModal
              schoolAdminToUpdate={schoolAdminToUpdate}
              updateSchoolAdminDistricts={updateSchoolAdminDistricts}
            />
            <SchoolAdminsDeleteButtonModal
              schoolAdminToDelete={schoolAdminToUpdate}
              removeSchoolAdmin={removeSchoolAdmin}
            />
          </React.Fragment>
        )
      },
      enableColumnFilter: false,
      enableSorting: false
    }
  ]

  const errorNodes = map(errors, (error, i: number) => <ErrorAlert key={i}>{error}</ErrorAlert>)

  const schoolAdminProfile =
    newSchoolAdmin !== null && newSchoolAdmin !== undefined ? (
      <div className={newSchoolAdminContainer}>
        School Admin added successfully!
        <SchoolAdminProfile schoolAdmin={newSchoolAdmin} />
      </div>
    ) : null

  return (
    <div className={schoolAdminsDataTableContainer}>
      <H1>School Admins</H1>
      {errorNodes}
      {schoolAdminProfile}
      <div className={`flex items-center ${actionBarStyle}`}>
        <SchoolAdminCreateButtonModal
          schoolAdminNewAdmin={schoolAdminNewAdmin}
          updateSchoolAdminSchools={updateSchoolAdminSchools}
        />
      </div>
      <DataTable
        columns={columns}
        fetchData={fetchData}
        data={schoolAdminsData}
        updateUrlOnFilter
        isLoading={isLoading}
        filterable
        manualFiltering
        defaultFiltered={defaultFiltered}
        defaultPageSize={100}
      />
    </div>
  )
}
